/**
 * tagRules.js — Iter 189d Phase C · Library UX polish
 *
 * Auto-classifier that assigns Density / Style / Industry tags to every
 * template. Templates that declare a `tags` object override the defaults.
 *
 * Dimensions:
 *   • density   → 'minimal' | 'editorial' | 'dense'
 *   • style     → 'warm' | 'cool' | 'bold'
 *   • industry  → 'studio' | 'retail' | 'nonprofit'
 *
 * The classifier is a light regex-over-id/label/category heuristic so we
 * don't have to manually tag 135 templates. Callers should ALSO honour
 * `tpl.tags` if present (explicit overrides win).
 */

// ── Density ─────────────────────────────────────────────────────────────
const DENSITY_MINIMAL_RX = /(minimal|slim|quiet|large-typography|centered-minimal|hero-minimal|compact|centered-hero|split-halfcover)/i;
const DENSITY_DENSE_RX   = /(grid-3|grid-4|8-up|quadgrid|bento|wall|multi-column|multi-column-dark|masonry|dense|two-by-two|three-col-cards|four-up|square-cards|mega-menu|full-bleed-scroll|marquee-gallery|carousel-scroll)/i;

function classifyDensity(id, label, catId) {
  if (DENSITY_MINIMAL_RX.test(id) || DENSITY_MINIMAL_RX.test(label)) return 'minimal';
  if (DENSITY_DENSE_RX.test(id) || DENSITY_DENSE_RX.test(label)) return 'dense';
  return 'editorial';
}

// ── Style ───────────────────────────────────────────────────────────────
const STYLE_WARM_RX = /(sunset|warm|palm-leaf|amber|orange|cream|beige|editorial-magazine|magazine|founder-letter|storytelling)/i;
const STYLE_BOLD_RX = /(bold|dark|violet|deep|black|brutalist|banner|dark-form|hero-dark|dark-mini|brand-showcase|large-typography|slim-promo-bar|founder-spotlight|dark-hero|dark-violet|multi-column-dark)/i;

function classifyStyle(id, label, catId) {
  if (STYLE_BOLD_RX.test(id) || STYLE_BOLD_RX.test(label)) return 'bold';
  if (STYLE_WARM_RX.test(id) || STYLE_WARM_RX.test(label)) return 'warm';
  return 'cool';
}

// ── Industry ────────────────────────────────────────────────────────────
const INDUSTRY_RETAIL_CATS   = new Set(['shop', 'pricing']);
const INDUSTRY_RETAIL_RX     = /(shop|product|cart|apparel|price|drop|store)/i;
const INDUSTRY_NONPROFIT_CATS = new Set(['donations', 'group-events']);
const INDUSTRY_NONPROFIT_RX  = /(donate|donation|impact|supporter|nonprofit|community|rsvp|event|schedule|conference|agenda)/i;

function classifyIndustry(id, label, catId) {
  if (INDUSTRY_RETAIL_CATS.has(catId) || INDUSTRY_RETAIL_RX.test(id) || INDUSTRY_RETAIL_RX.test(label)) return 'retail';
  if (INDUSTRY_NONPROFIT_CATS.has(catId) || INDUSTRY_NONPROFIT_RX.test(id) || INDUSTRY_NONPROFIT_RX.test(label)) return 'nonprofit';
  return 'studio';
}

/**
 * Compute the tag triple for a template. Explicit `tpl.tags` fields win
 * over the heuristic classifier.
 *
 * @param {{id?:string, label?:string, tags?:object, _categoryId?:string}} tpl
 * @param {string} catId
 * @returns {{density:string, style:string, industry:string}}
 */
export function tagsFor(tpl, catId) {
  const id = tpl?.id || '';
  const label = tpl?.label || '';
  const cid = catId || tpl?._categoryId || '';
  const explicit = tpl?.tags || {};
  return {
    density:  explicit.density  || classifyDensity(id, label, cid),
    style:    explicit.style    || classifyStyle(id, label, cid),
    industry: explicit.industry || classifyIndustry(id, label, cid),
  };
}

/**
 * Given a template and the active filter selection, return true if the
 * template should be visible. Each dimension is an OR (multi-select
 * within), all dimensions AND together. Empty set on a dimension = no
 * filter for that dimension.
 */
export function matchesFilters(tpl, catId, filters) {
  const t = tagsFor(tpl, catId);
  if (filters.density?.size  && !filters.density.has(t.density))   return false;
  if (filters.style?.size    && !filters.style.has(t.style))       return false;
  if (filters.industry?.size && !filters.industry.has(t.industry)) return false;
  return true;
}

// ── Filter chip config ──────────────────────────────────────────────────
export const FILTER_DIMENSIONS = [
  {
    key: 'density',
    label: 'Density',
    options: [
      { id: 'minimal',   label: 'Minimal' },
      { id: 'editorial', label: 'Editorial' },
      { id: 'dense',     label: 'Dense' },
    ],
  },
  {
    key: 'style',
    label: 'Style',
    options: [
      { id: 'warm', label: 'Warm' },
      { id: 'cool', label: 'Cool' },
      { id: 'bold', label: 'Bold' },
    ],
  },
  {
    key: 'industry',
    label: 'Industry',
    options: [
      { id: 'studio',    label: 'Studio' },
      { id: 'retail',    label: 'Retail' },
      { id: 'nonprofit', label: 'Nonprofit' },
    ],
  },
];
